import { QRCode } from 'react-qr-code'
import styled, { useTheme } from 'styled-components'

import { Network } from '@root/entities/network'
import { WalletModelShort } from '@root/entities/wallet/ui'
import { useTranslation } from '@root/shared/localization'
import { Separator } from '@root/shared/ui-kit'

import { WalletAddress } from './wallet-address'

import type { WalletModel } from '@root/entities/wallet/atoms'
import type { ClassNameProps } from '@root/shared/lib/react'

type Props = { wallet: WalletModel } & ClassNameProps

export const WalletDeposit: React.FC<Props> = ({
  wallet,
  className,
  ...props
}) => {
  const theme = useTheme()
  const { t } = useTranslation()

  return (
    <Root className={className} {...props}>
      <Top>
        <WalletModelShort size="sm" wallet={wallet} />
      </Top>
      <Body>
        <QrCodeContainer>
          <QRCode
            size={180}
            value={wallet.address}
            bgColor={theme.palette.gray[2]}
            fgColor={theme.palette.gray[11]}
          />
        </QrCodeContainer>
      </Body>
      <Footer>
        <WalletAddress address={wallet.address} />
        <Sep />
        <Warning>
          {t('wallet.deposit', {
            network: Network.toDisplayString(wallet.network).toUpperCase(),
          })}
        </Warning>
      </Footer>
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  gap: 22px;
`
const Top = styled.div`
  display: flex;
  justify-content: center;
`
const Body = styled.div`
  position: relative;
  justify-self: center;

  border-radius: 20px;
  padding: 30px;
  border: 2px solid var(--primary-9);

  &::after,
  &::before {
    content: '';
    position: absolute;

    border: solid var(--background-2) 2px;
    border-radius: 2px;
  }

  &::before {
    left: -2px;
    right: -2px;
    top: 30px;
    bottom: 30px;
    border-top-color: transparent;
    border-bottom-color: transparent;
  }

  &::after {
    top: -2px;
    bottom: -2px;
    left: 30px;
    right: 30px;
    border-left-color: transparent;
    border-right-color: transparent;
  }
`

const Footer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`
const Warning = styled.div`
  color: var(--text-9);

  font-size: 11px;
  line-height: 16px;
  text-align: center;
`

const QrCodeContainer = styled.div`
  position: relative;
  display: grid;
  justify-items: center;
  align-items: center;
`
const Sep = styled(Separator)`
  background: var(--background-2);
`
